<template>
  <v-my-layout>
    <v-my-card slot="body">
      <v-tabs>
        <v-tab to="#information">
          {{ $t("general.admin.tabs.devices.information") }}
        </v-tab>
        <v-tab
          v-if="device.id"
          to="#errors"
        >
          {{ $t("general.admin.tabs.devices.errors") }}
        </v-tab>
        <v-tabs-items>
          <v-tab-item value="information">
            <div
              v-if="$apollo.queries.device.loading"
              class="loading"
            >
              <v-progress-circular
                indeterminate
                color="#193b95"
              />
            </div>
            <v-card-title>
              <span
                v-if="!$route.params.id"
                class="headline"
              >
                {{ $t('general.admin.tabs.devices.new') }}
              </span>
              <span
                v-else
                class="headline"
              >
                {{ $t('general.admin.tabs.devices.edit') }}
              </span>
            </v-card-title>

            <v-card-text>
              <v-container
                grid-list-md
                pa-0
              >
                <v-layout wrap>
                  <v-flex xs12>
                    <v-text-field
                      v-model="device.key"
                      :label="$t('general.forms.device_id')"
                      :rules="textInputRules"
                      :hint="$t('general.forms.required')"
                    />
                    <v-text-field
                      v-model="device.name"
                      :label="$t('general.forms.name')"
                      :rules="textInputRules"
                      :hint="$t('general.forms.required')"
                    />
                    <v-select
                      v-model="device.company"
                      :items="companies.map(company => ({
                        value: company.id,
                        text: company.name,
                      }))"
                      :label="$t('general.forms.company')"
                      :no-data-text="$t('general.forms.no_data_text')"
                    />
                    <v-select
                      v-model="device.projectId"
                      :items="companyProjects"
                      :label="$t('general.forms.project')"
                      :no-data-text="$t('general.forms.no_data_text')"
                    />
                    <v-text-field
                      :value="device.mac || $t('general.dialogs.unknown')"
                      :label="$t('general.forms.mac')"
                      readonly
                      class='readonly'
                    />
                    <v-text-field
                      :value="device.firmwareVersion || $t('general.dialogs.unknown')"
                      :label="$t('general.forms.firmwareVersion')"
                      readonly
                      class='readonly'
                    />
                    <v-textarea
                      :value="JSON.stringify(device.hardwareConfiguration, null, '  ') || $t('general.dialogs.unknown')"
                      :label="$t('general.forms.hardwareConfiguration')"
                      readonly
                      class='readonly'
                    />
                    <v-btn
                      @click="toggleSampleIntervalDialog"
                    >
                      {{ $t('general.forms.setSampleInterval') }}
                    </v-btn>
                    <v-btn
                      @click="togglePublishIntervalDialog"
                    >
                      {{ $t('general.forms.setPublishInterval') }}
                    </v-btn>
                  </v-flex>
                  <v-flex
                    v-show="showFormError"
                    xs12
                  >
                    {{ $t('general.dialogs.error') }}
                  </v-flex>
                </v-layout>
                <h2>{{ $t('general.pump.parameter.plural') }}</h2>
                <v-tabs v-model="activePump">
                  <v-tab
                    v-for="collectionId in pumpCollections"
                    :key="collectionId"
                  >
                    {{ $t('general.pump.name') }} {{ collectionId }}
                  </v-tab>
                  <v-tab-item
                    v-for="collectionId in pumpCollections"
                    :key="collectionId"
                  >
                    <v-pump-parameter
                      v-for="setting in device.device_pump_settings.filter(setting => setting.collectionId === collectionId).sort(orderSort)"
                      :key="setting.id"
                      :setting="setting"
                      :on-delete="removeParameter"
                      :on-increase-sort="(id) => increaseSort(id, collectionId)"
                      :on-decrease-sort="(id) => decreaseSort(id, collectionId)"
                    />
                    <v-btn
                      flat
                      @click="() => addNewParameter(collectionId)"
                    >
                      {{ $t('general.pump.new_parameter') }}
                    </v-btn>
                  </v-tab-item>
                </v-tabs>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-btn
                color="blue darken-1"
                flat
                @click="$router.push('/admin#devices')"
              >
                {{ $t('general.dialogs.cancel') }}
              </v-btn>
              <v-btn
                :disabled="!device.name || !device.key || !device.company || submissionLoading"
                color="blue darken-1"
                flat
                @click="submit"
              >
                {{ $t('general.dialogs.save') }}
              </v-btn>
            </v-card-actions>
          </v-tab-item>
          <v-tab-item
            v-if="device.id"
            value="errors"
          >
            <v-error-table
              :device-id="device.id"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-tabs>
    </v-my-card>
    <v-dialog
      v-model="showSampleIntervalDialog"
    >
      <v-card>
        <v-card-text>
          <v-text-field
            v-model="newSampleInterval"
            :label="$t('general.forms.sampleInterval')"
            type="number"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="success"
            @click="updateSampleInterval"
          >
            {{ $t('general.dialogs.save') }}
          </v-btn>
          <v-btn
            color="error"
            @click="toggleSampleIntervalDialog"
          >
            {{ $t('general.dialogs.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showPublishIntervalDialog"
    >
      <v-card>
        <v-card-text>
          <v-text-field
            v-model="newPublishInterval"
            :label="$t('general.forms.publishInterval')"
            type="number"
          />
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="success"
            @click="updatePublishInterval"
          >
            {{ $t('general.dialogs.save') }}
          </v-btn>
          <v-btn
            color="error"
            @click="togglePublishIntervalDialog"
          >
            {{ $t('general.dialogs.cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-my-layout>
</template>

<script>
import VMyLayout from '@/layouts/Default.vue';
import VMyCard from '@/components/Card.vue';
import VPumpParameter from '@/components/PumpParameter.vue';
import gql from 'graphql-tag';
import { v4 as uuid } from 'uuid';
import sendCommandMutation from '@/queries/sendCommand';
import { createDeviceMutation, updateDeviceMutation } from '@/queries/deviceMutations';
import VErrorTable from '@/components/ErrorTable.vue';

const collectionsForCommands = [0, 1, 2, 3, 4];

function createDefaultsForCollection(collectionId) {
  return [
    {
      id: `new-${uuid()}`,
      collectionId,
      key: 'ClO2_IST',
      label: 'Dosiermedium IST',
      type: 'text',
      unit: 'ppm',
      visible: true,
      editable: false,
      formatting: '0.00',
      sortOrder: 0,
    },
    {
      id: `new-${uuid()}`,
      collectionId,
      key: 'Fasstemperatur',
      label: 'Fasstemperatur',
      type: 'text',
      unit: '°C',
      visible: true,
      editable: false,
      formatting: '0.0',
      sortOrder: 1,
    },
    {
      id: `new-${uuid()}`,
      collectionId,
      key: 'Füllstand',
      label: 'Füllstand Behälter',
      type: 'text',
      unit: 'L',
      visible: true,
      editable: false,
      formatting: '0.00',
      sortOrder: 2,
    },
    {
      id: `new-${uuid()}`,
      collectionId,
      key: 'Medium dosiert',
      label: 'Medium Dosiert',
      type: 'text',
      unit: 'L',
      visible: true,
      editable: false,
      formatting: '0.00',
      sortOrder: 3,
    },
    {
      id: `new-${uuid()}`,
      collectionId,
      key: 'Status',
      label: 'Status',
      type: 'bool',
      unit: '',
      visible: true,
      editable: true,
      sortOrder: 4,
    },
    {
      id: `new-${uuid()}`,
      collectionId,
      key: 'Dosierleistung',
      label: 'Dosierleistung',
      type: 'text',
      unit: 'ml/h',
      visible: true,
      editable: false,
      formatting: '0.0',
      sortOrder: 5,
    },
    {
      id: `new-${uuid()}`,
      collectionId,
      key: 'Stellwert',
      label: 'Dosiermedium SOLL',
      type: 'text',
      unit: 'ppm',
      visible: true,
      editable: true,
      formatting: '0.00',
      sortOrder: 6,
    },
    {
      id: `new-${uuid()}`,
      collectionId,
      key: 'Betriebsmodus',
      label: 'Betriebsmodus',
      type: 'text',
      unit: '',
      visible: true,
      editable: true,
      formatting: '0',
      sortOrder: 7,
    },
  ];
}

const nullDevice = {
  key: '',
  name: '',
  company: '',
  projectId: undefined,
  device_pump_settings: [],
};

export default {
  name: 'DeviceSettings',
  components: {
    VMyLayout,
    VMyCard,
    VPumpParameter,
    VErrorTable,
  },
  data() {
    return {
      textInputRules: [value => !!value || this.$t('general.forms.required')],
      showFormError: false,
      device: { ...nullDevice },
      companies: [],
      activePump: 0,
      pumpCollections: [
        '1',
        '2',
        '3',
        '4',
      ],
      removedSettings: [],
      newSampleInterval: null,
      newPublishInterval: null,
      showSampleIntervalDialog: false,
      showPublishIntervalDialog: false,
      submissionLoading: false,
    };
  },

  computed: {
    companyProjects() {
      const deviceCompany = this.companies.find(company => company.id === this.device.company);
      if (deviceCompany) {
        const companyProjects = deviceCompany.projects;
        if (companyProjects) {
          return companyProjects.map(project => ({
            value: project.id,
            text: project.name,
          }));
        }
      }
      return [];
    },
    basicVariables() {
      return {
        key: this.device.key,
        name: this.device.name,
        groupId: this.device.company,
        projectId: this.device.projectId,
      };
    },
  },

  mounted() {
    if (!this.$route.params.id) {
      this.device.device_pump_settings = this.pumpCollections.reduce((allCollections, collection) => [
        ...allCollections,
        ...createDefaultsForCollection(collection),
      ], []);
    }
  },

  apollo: {
    companies: {
      query: gql`
        query GetAllCompanies {
          group {
            name
            id
            projects {
              id
              name
            }
          }
        }
      `,
      update: data => data.group,
    },
    device: {
      query: gql`
        query GetDevice (
          $id: uuid
        ) {
          device (
            where: {
              id: {
                _eq: $id
              }
            }
          ) {
            id
            key
            name
            projectId
            mac
            firmwareVersion
            hardwareConfiguration
            group {
              id
            }
            device_pump_settings {
              id
              collectionId
              key
              label
              type
              unit
              visible
              editable
              formatting
              sortOrder
            }
          }
        }
      `,
      variables() {
        return {
          id: this.$route.params.id,
        };
      },
      skip() {
        return !this.$route.params.id;
      },
      update(data) {
        const foundDevice = data.device[0];
        if (foundDevice) {
          return {
            ...foundDevice,
            company: foundDevice.group.id,
          };
        }
        return nullDevice;
      },
      error(error) {
        this.$root.vtoast.show({
          message: `${this.$t('general.admin.tabs.devices.error')}: ${error}`,
          color: 'error',
        });
      },
    },
  },

  methods: {
    async intervalCommand(commandName, value) {
      const promises = collectionsForCommands.map(collection => this.$apollo.mutate({
        mutation: sendCommandMutation,
        variables: {
          deviceId: this.device.key,
          name: commandName,
          parameters: JSON.stringify([collection, value]),
        },
      }));
      await Promise.all(promises);
    },
    async updateSampleInterval() {
      try {
        await this.intervalCommand('setSampleInterval', parseInt(this.newSampleInterval, 10));
        this.toggleSampleIntervalDialog();
      } catch (e) {
        window.alert(e.message);
      }
    },
    async updatePublishInterval() {
      try {
        await this.intervalCommand('setPublishInterval', parseInt(this.newPublishInterval, 10));
        this.togglePublishIntervalDialog();
      } catch (e) {
        window.alert(e.message);
      }
    },
    toggleSampleIntervalDialog() {
      this.showSampleIntervalDialog = !this.showSampleIntervalDialog;
    },
    togglePublishIntervalDialog() {
      this.showPublishIntervalDialog = !this.showPublishIntervalDialog;
    },
    orderSort(a, b) {
      if (a.sortOrder > b.sortOrder) return 1;
      if (b.sortOrder > a.sortOrder) return -1;
      return 0;
    },
    increaseSort(id, collectionId) {
      const allParams = this.device.device_pump_settings.filter(setting => setting.collectionId === collectionId).sort(this.orderSort);
      const thisIndex = allParams.findIndex(param => param.id === id);
      if (thisIndex !== -1 && allParams.length > thisIndex + 1) {
        const swapTargetId = allParams[thisIndex + 1].id;
        const oldSortOrder = allParams[thisIndex].sortOrder;
        const newSortOrder = allParams[thisIndex + 1].sortOrder;

        this.device.device_pump_settings = this.device.device_pump_settings.map((param) => {
          if (param.id === id) {
            return {
              ...param,
              sortOrder: newSortOrder,
            };
          }
          if (param.id === swapTargetId) {
            return {
              ...param,
              sortOrder: oldSortOrder,
            };
          }
          return param;
        });
      }
    },
    decreaseSort(id, collectionId) {
      const allParams = this.device.device_pump_settings.filter(setting => setting.collectionId === collectionId).sort(this.orderSort);
      const thisIndex = allParams.findIndex(param => param.id === id);
      if (thisIndex !== -1 && thisIndex > 0) {
        const swapTargetId = allParams[thisIndex - 1].id;
        const oldSortOrder = allParams[thisIndex].sortOrder;
        const newSortOrder = allParams[thisIndex - 1].sortOrder;

        this.device.device_pump_settings = this.device.device_pump_settings.map((param) => {
          if (param.id === id) {
            return {
              ...param,
              sortOrder: newSortOrder,
            };
          }
          if (param.id === swapTargetId) {
            return {
              ...param,
              sortOrder: oldSortOrder,
            };
          }
          return param;
        });
      }
    },
    removeParameter(id) {
      this.removedSettings = [
        ...this.removedSettings,
        id,
      ];
      this.device.device_pump_settings = this.device.device_pump_settings.filter(setting => setting.id !== id);
    },
    addNewParameter(collectionId) {
      const maxOrder = this.device.device_pump_settings
        .filter(param => param.collectionId === collectionId)
        .reduce(
          (max, param) => Math.max(param.sortOrder, max),
          -1,
        );
      this.device.device_pump_settings = [
        ...this.device.device_pump_settings,
        {
          id: `new-${uuid()}`,
          key: '',
          label: '',
          type: '',
          unit: '',
          visible: true,
          editable: false,
          formatting: '',
          collectionId,
          sortOrder: maxOrder + 1,
        },
      ];
    },
    async submit() {
      this.submissionLoading = true;
      try {
        if (this.$route.params.id) {
          await this.updateDevice();
        } else {
          await this.createDevice();
        }
      } catch (e) {
        this.$root.vtoast.show({
          message: e.message,
          color: 'error',
        });
      } finally {
        this.submissionLoading = false;
      }
    },
    async updateParams(deviceId) {
      const pumpPromises = [];

      // New Settings
      this.device.device_pump_settings
        .filter(setting => setting.id.startsWith('new-'))
        .forEach((setting) => {
          pumpPromises.push(this.$apollo.mutate({
            mutation: gql`
              mutation(
                $collectionId: String!
                $key: String!
                $label: String!
                $type: String!
                $unit: String!
                $visible: Boolean!
                $editable: Boolean!
                $formatting: String
                $sortOrder: Int!
                $deviceId: uuid!
              ) {
                insert_device_pump_settings(
                  objects: {
                    collectionId: $collectionId
                    key: $key
                    label: $label
                    type: $type
                    unit: $unit
                    visible: $visible
                    editable: $editable
                    formatting: $formatting
                    sortOrder: $sortOrder
                    deviceId: $deviceId
                  }
                ) {
                  affected_rows
                }
              }
            `,
            variables: {
              collectionId: setting.collectionId,
              key: setting.key,
              label: setting.label,
              type: setting.type,
              unit: setting.unit,
              visible: setting.visible,
              editable: setting.editable,
              formatting: setting.formatting,
              sortOrder: setting.sortOrder,
              deviceId,
            },
          }));
        });

      // Modified settings
      this.device.device_pump_settings
        .filter(setting => !setting.id.startsWith('new-') && !this.removedSettings.includes(setting.id))
        .forEach((setting) => {
          pumpPromises.push(this.$apollo.mutate({
            mutation: gql`
              mutation(
                $id: uuid!
                $collectionId: String!
                $key: String!
                $label: String!
                $type: String!
                $unit: String!
                $visible: Boolean!
                $editable: Boolean!
                $formatting: String
                $sortOrder: Int!
                $deviceId: uuid!
              ) {
                update_device_pump_settings(
                  where: {
                    id: { _eq: $id }
                  }
                  _set: {
                    collectionId: $collectionId
                    key: $key
                    label: $label
                    type: $type
                    unit: $unit
                    visible: $visible
                    editable: $editable
                    formatting: $formatting
                    sortOrder: $sortOrder
                    deviceId: $deviceId
                  }
                ) {
                  affected_rows
                }
              }
            `,
            variables: {
              id: setting.id,
              collectionId: setting.collectionId,
              key: setting.key,
              label: setting.label,
              type: setting.type,
              unit: setting.unit,
              visible: setting.visible,
              editable: setting.editable,
              formatting: setting.formatting,
              sortOrder: setting.sortOrder,
              deviceId,
            },
          }));
        });

      // Deleted settings
      this.removedSettings
        .forEach((settingId) => {
          pumpPromises.push(this.$apollo.mutate({
            mutation: gql`
              mutation(
                $id: uuid!
              ) {
                delete_device_pump_settings(
                  where: {
                    id: { _eq: $id }
                  }
                ) {
                  affected_rows
                }
              }
            `,
            variables: {
              id: settingId,
            },
          }));
        });

      await Promise.all(pumpPromises);
    },
    async updateDevice() {
      try {
        await this.$apollo.mutate({
          mutation: updateDeviceMutation,
          variables: {
            id: this.$route.params.id,
            ...this.basicVariables,
          },
        });

        await this.updateParams(this.$route.params.id);
        this.$root.vtoast.show({ message: this.$t("general.admin.tabs.devices.updated") });
      } catch (e) {
        this.$root.vtoast.show({
          message: e.message,
          color: 'error',
        });
      }
    },
    async createDevice() {
      try {
        const response = await this.$apollo.mutate({
          mutation: createDeviceMutation,
          variables: this.basicVariables,
        });
        if (response) {
          const deviceId = response.data.insert_device.returning[0].id;
          await this.updateParams(deviceId);
          this.$router.replace(`/admin/device/${deviceId}`);
          this.$root.vtoast.show({ message: this.$t("general.admin.tabs.devices.created") });
        }
      } catch (e) {
        this.$root.vtoast.show({
          message: e.message,
          color: 'error',
        });
      }
    },
  },
};
</script>

<style scoped>
.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 430px;
  position: absolute;
  background-color: rgba(0, 0, 0, 0.5);
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  border-radius: 10px;
}

.saveError {
  color: red;
}

.readonly {
  opacity: 0.5;
}
</style>
